import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Sparkles, X, Send, Phone, ArrowRight } from "lucide-react";
import {
  GLEAN_DEMO_EVALUATION_ID,
  DISCOVERY_QUESTIONS,
  buildTailoredResponse,
  GENERIC_INITIAL_MESSAGE,
  GENERIC_RESPONSES,
  type DiscoveryAnswers,
} from "@/data/glean-mock";

// =============================================================================
// Types
// =============================================================================

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  content: string;
  /** Clickable option chips rendered below the message */
  options?: string[];
  /** Inline CTA button */
  cta?: { label: string; icon?: "phone" | "arrow"; action: "vendor-call" };
}

type ConversationPhase =
  | "idle"
  | "discovery-budget"
  | "discovery-priority"
  | "discovery-familiarity"
  | "thinking"
  | "recommendation"
  | "complete";

// =============================================================================
// Helpers
// =============================================================================

let messageIdCounter = 0;
function nextId(): string {
  return `msg-${++messageIdCounter}`;
}

/** Check if the current route is a Glean report page. */
function isGleanReportPage(pathname: string): boolean {
  return (
    pathname.includes(`/reports/static/${GLEAN_DEMO_EVALUATION_ID}`) ||
    pathname.includes(`/reports/experience/${GLEAN_DEMO_EVALUATION_ID}`) ||
    pathname.includes(`/reports/infographic/${GLEAN_DEMO_EVALUATION_ID}`)
  );
}

// =============================================================================
// Component
// =============================================================================

export default function AIAssistantOverlay() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [phase, setPhase] = useState<ConversationPhase>("idle");
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [answers, setAnswers] = useState<Partial<DiscoveryAnswers>>({});
  const [hasInitialized, setHasInitialized] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevPathnameRef = useRef(location.pathname);

  const isGlean = isGleanReportPage(location.pathname);

  // ---------------------------------------------------------------------------
  // Auto-scroll to bottom on new messages
  // ---------------------------------------------------------------------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // ---------------------------------------------------------------------------
  // Listen for external "open assistant" event (e.g. scroll-to-bottom trigger)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleOpenAssistant = () => {
      if (!isOpen) {
        setIsOpen(true);
      }
    };
    window.addEventListener("open-ai-assistant", handleOpenAssistant);
    return () => window.removeEventListener("open-ai-assistant", handleOpenAssistant);
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Reset conversation when navigating to a different page
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      setMessages([]);
      setPhase("idle");
      setAnswers({});
      setHasInitialized(false);
      setIsTyping(false);
    }
  }, [location.pathname]);

  // ---------------------------------------------------------------------------
  // Initialize conversation when panel opens
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || hasInitialized) return;
    setHasInitialized(true);

    if (isGlean) {
      // Start Glean discovery flow
      const q = DISCOVERY_QUESTIONS[0];
      simulateAssistantMessage(q.message, q.options);
      setPhase("discovery-budget");
    } else {
      // Generic greeting
      simulateAssistantMessage(GENERIC_INITIAL_MESSAGE);
      setPhase("idle");
    }
  }, [isOpen, hasInitialized, isGlean]);

  // ---------------------------------------------------------------------------
  // Simulated typing delay then push assistant message
  // ---------------------------------------------------------------------------
  const simulateAssistantMessage = useCallback(
    (
      content: string,
      options?: string[],
      cta?: ChatMessage["cta"],
      delay = 800,
    ) => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: "assistant", content, options, cta },
        ]);
      }, delay);
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Handle option chip click (discovery flow)
  // ---------------------------------------------------------------------------
  const handleOptionClick = useCallback(
    (option: string) => {
      // Add user message
      setMessages((prev) => [
        ...prev.map((m) => ({ ...m, options: undefined })), // remove chips from previous messages
        { id: nextId(), role: "user" as const, content: option },
      ]);

      if (phase === "discovery-budget") {
        const newAnswers = { ...answers, budget: option };
        setAnswers(newAnswers);
        const q = DISCOVERY_QUESTIONS[1];
        simulateAssistantMessage(q.message, q.options);
        setPhase("discovery-priority");
      } else if (phase === "discovery-priority") {
        const newAnswers = { ...answers, priority: option };
        setAnswers(newAnswers);
        const q = DISCOVERY_QUESTIONS[2];
        simulateAssistantMessage(q.message, q.options);
        setPhase("discovery-familiarity");
      } else if (phase === "discovery-familiarity") {
        const finalAnswers: DiscoveryAnswers = {
          budget: answers.budget || "Still exploring",
          priority: answers.priority || "AI transparency",
          familiarity: option,
        };
        setAnswers(finalAnswers);
        setPhase("thinking");

        // Show "thinking" then deliver tailored response
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          const response = buildTailoredResponse(finalAnswers);

          // Issues message
          setMessages((prev) => [
            ...prev,
            { id: nextId(), role: "assistant", content: response.issuesMessage },
          ]);

          // Questions message (staggered)
          setTimeout(() => {
            setIsTyping(true);
            setTimeout(() => {
              setIsTyping(false);
              setMessages((prev) => [
                ...prev,
                {
                  id: nextId(),
                  role: "assistant",
                  content: response.questionsMessage,
                },
              ]);

              // Alignment + CTA (staggered)
              setTimeout(() => {
                setIsTyping(true);
                setTimeout(() => {
                  setIsTyping(false);
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: nextId(),
                      role: "assistant",
                      content: response.alignmentMessage,
                      cta: {
                        label: "Plan Vendor Call",
                        icon: "phone",
                        action: "vendor-call",
                      },
                    },
                  ]);
                  setPhase("complete");
                }, 600);
              }, 300);
            }, 1000);
          }, 400);
        }, 1500);
      }
    },
    [phase, answers, simulateAssistantMessage],
  );

  // ---------------------------------------------------------------------------
  // Handle free-text input (generic pages or follow-up on Glean)
  // ---------------------------------------------------------------------------
  const handleSendMessage = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = inputValue.trim();
      if (!text) return;

      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: "user", content: text },
      ]);
      setInputValue("");

      if (isGlean && phase === "complete") {
        // Follow-up on Glean report — generic helpful response
        simulateAssistantMessage(
          "That's a great point. I'd suggest adding that to your vendor call agenda. You can capture it in the questionnaire once you start the call.",
          undefined,
          {
            label: "Go to Vendor Call",
            icon: "arrow",
            action: "vendor-call",
          },
        );
      } else {
        // Generic pages
        const lower = text.toLowerCase();
        let response = GENERIC_RESPONSES.default;
        if (lower.includes("help") || lower.includes("what can you do")) {
          response = GENERIC_RESPONSES.help;
        } else if (
          lower.includes("framework") ||
          lower.includes("criteria") ||
          lower.includes("see") ||
          lower.includes("change") ||
          lower.includes("adapt")
        ) {
          response = GENERIC_RESPONSES.framework;
        }
        simulateAssistantMessage(response);
      }
    },
    [inputValue, isGlean, phase, simulateAssistantMessage],
  );

  // ---------------------------------------------------------------------------
  // Handle CTA click
  // ---------------------------------------------------------------------------
  const handleCtaClick = useCallback(() => {
    setIsOpen(false);
    navigate(isGlean ? "/vendor-call?vendor=glean-demo" : "/vendor-call");
  }, [navigate, isGlean]);

  // ---------------------------------------------------------------------------
  // Toggle panel
  // ---------------------------------------------------------------------------
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Only render on Glean demo report pages
  if (!isGlean) return null;

  // ---------------------------------------------------------------------------
  // Render markdown-like bold (**text**)
  // ---------------------------------------------------------------------------
  const renderContent = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      // Handle newlines
      return part.split("\n").map((line, j, arr) => (
        <span key={`${i}-${j}`}>
          {line}
          {j < arr.length - 1 && <br />}
        </span>
      ));
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* FAB Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleToggle}
            className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] text-white shadow-lg shadow-[#EAA059]/30 flex items-center justify-center"
            aria-label="Open AI Assistant"
          >
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full bg-[#EAA059]/40 animate-ping" />
            <Sparkles className="w-6 h-6 relative z-10" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop (click to close) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-[2px]"
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed bottom-3 left-3 right-3 sm:left-auto sm:bottom-6 sm:right-6 z-[70] w-auto sm:w-[420px] max-h-[600px] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-[#1a6394]/50"
              style={{ maxHeight: "min(600px, calc(100vh - 48px))" }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center shadow-md flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-sm">
                    FeedForward Assistant
                  </h3>
                  <p className="text-slate-300 text-xs truncate">
                    {isGlean
                      ? "Analyzing Glean evaluation"
                      : "Here to help"}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2.5 rounded-lg hover:bg-white/10 transition-colors text-slate-300 hover:text-white"
                  aria-label="Close assistant"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto bg-[#f8f9fb] dark:bg-slate-900 p-4 space-y-4 min-h-0">
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {/* Assistant avatar */}
                    {msg.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}

                    <div
                      className={`max-w-[85%] space-y-2 ${msg.role === "user" ? "items-end" : "items-start"}`}
                    >
                      {/* Message bubble */}
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "bg-gradient-to-r from-[#EAA059] to-[#d4914d] text-white rounded-tr-md"
                            : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 shadow-sm border border-slate-100 dark:border-slate-700 rounded-tl-md"
                        }`}
                      >
                        {renderContent(msg.content)}
                      </div>

                      {/* Option chips */}
                      {msg.options && msg.options.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {msg.options.map((opt) => (
                            <button
                              key={opt}
                              onClick={() => handleOptionClick(opt)}
                              className="px-3 py-1.5 text-xs font-medium rounded-full border border-[#EAA059]/40 bg-[#EAA059]/5 text-[#b47a3a] dark:text-[#EAA059] hover:bg-[#EAA059]/15 hover:border-[#EAA059]/60 transition-all duration-200"
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* CTA button */}
                      {msg.cta && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3, type: "spring" }}
                          onClick={handleCtaClick}
                          className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#EAA059] to-[#d4914d] text-white text-sm font-semibold shadow-md shadow-[#EAA059]/20 hover:shadow-lg hover:shadow-[#EAA059]/30 hover:brightness-110 transition-all duration-200"
                        >
                          {msg.cta.icon === "phone" ? (
                            <Phone className="w-4 h-4" />
                          ) : (
                            <ArrowRight className="w-4 h-4" />
                          )}
                          {msg.cta.label}
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-2.5"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border border-slate-100 dark:border-slate-700">
                      <div className="flex gap-1.5">
                        <span
                          className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        />
                        <span
                          className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                          style={{ animationDelay: "150ms" }}
                        />
                        <span
                          className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                          style={{ animationDelay: "300ms" }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <form
                onSubmit={handleSendMessage}
                className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-4 py-3 flex gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={
                    isGlean && phase !== "complete"
                      ? "Select an option above..."
                      : "Ask me anything..."
                  }
                  disabled={isGlean && phase !== "complete" && phase !== "idle"}
                  className="flex-1 px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:border-[#EAA059] focus:outline-none focus:ring-1 focus:ring-[#EAA059]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                />
                <button
                  type="submit"
                  disabled={
                    !inputValue.trim() ||
                    isTyping ||
                    (isGlean && phase !== "complete" && phase !== "idle")
                  }
                  className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#EAA059] to-[#d4914d] text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
